import React, { useState, useEffect } from "react";
import { Table, Select, Radio, Spin, Empty, message } from "antd";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../helpers/axios";
import { formatToLocalDate } from "../../../helpers/utils";
import { svg } from "../../../Assets/svg";
import { getDecryptedName } from "../../../helpers/bycrypt";

interface IssueItem {
  _id: string;
  issue_no?: number;
  quote_request_id: string;
  display_quote_request_id: string;
  title: string;
  description: string;
  type: "Issue" | "Blocked";
  status: "Open" | "In Progress" | "Resolved";
  created_at: string;
  created_by: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

export default function IssueBlockTable() {
  const navigate = useNavigate();
  const [data, setData] = useState<IssueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("Open/In-Progress");
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/quote-requests/issues/all", {
        params: {
          status: statusFilter,
          type: typeFilter
        }
      });
      if (response.data.success) {
        setData(response.data.data.issues || []);
      }
    } catch (error) {
      console.error("Failed to fetch issues:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [statusFilter, typeFilter]);

  const columns = [
    {
      title: "Request ID",
      dataIndex: "display_quote_request_id",
      key: "display_quote_request_id",
      render: (text: string, record: IssueItem) => (
        <span 
          className="text-primary fw-semibold cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/quote-requests/${record.quote_request_id}`);
          }}
        >
          {text}
        </span>
      )
    },
    {
      title: "Issue No",
      key: "issue_no",
      render: (record: IssueItem) => record.issue_no ? `#${record.issue_no}` : "-"
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => {
        const isBlocked = type === "Blocked";
        return (
          <span className={`d-inline-flex align-items-center billStatus ${isBlocked ? "status-blocked" : "status-issue"}`}>
            {svg.dotIcon}
            {type === "Blocked" ? "Blocked" : "Issue"}
          </span>
        );
      }
    },
    {
      title: "Title / Content",
      dataIndex: "title",
      key: "title",
      className: "fw-semibold text-dark"
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc: string) => desc || "-"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        let className = "status-todo";
        if (status === "In Progress") className = "status-inprogress";
        if (status === "Resolved") className = "status-completed";

        return (
          <span className={`d-inline-flex align-items-center billStatus ${className}`}>
            {svg.dotIcon}
            {status}
          </span>
        );
      }
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      key: "created_by",
      render: (created_by: any) => {
        if (!created_by) return "-";
        return getDecryptedName(created_by) || "-";
      }
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (val ? formatToLocalDate(val) : "-")
    },
    {
      title: "Change Status",
      key: "change_status",
      render: (record: IssueItem) => (
        <Select
          value={record.status}
          onChange={async (newStatus) => {
            try {
              const response = await axiosInstance.put(
                `/quote-requests/${record.quote_request_id}/issues/${record._id}/status`,
                { status: newStatus }
              );
              if (response.data.success) {
                message.success("Status updated successfully");
                fetchIssues();
              } else {
                message.error(response.data.message || "Failed to update status");
              }
            } catch (error: any) {
              console.error(error);
              if (error?.response?.status === 417) {
                message.info({
                  content: (
                    <span>
                      This quote request has already been converted to{" "}
                      <strong>Quote Created</strong>. So, cannot be edited.
                    </span>
                  ),
                  key: "status_lock_redirect_toast",
                });
                fetchIssues();
                return;
              }
              message.error(error.response?.data?.message || "Failed to update status");
            }
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ width: "130px" }}
          options={[
            { value: "Open", label: "Open" },
            { value: "In Progress", label: "In Progress" },
            { value: "Resolved", label: "Resolved" }
          ]}
        />
      )
    }
  ];

  return (
    <div className="d-flex flex-column h-100 p-2">
      {/* Filters Toolbar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 border-bottom pb-3">
        {/* Status Radio Buttons (tabs) */}
        <div>
          <Radio.Group 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="large"
          >
            <Radio.Button value="Open/In-Progress">Open / In Progress</Radio.Button>
            <Radio.Button value="Resolved">Resolved</Radio.Button>
            <Radio.Button value="All">All</Radio.Button>
          </Radio.Group>
        </div>

        {/* Type Filter Select Dropdown */}
        <div className="d-flex align-items-center gap-2">
          <span className="small text-muted fw-semibold">Type:</span>
          <Select
            value={typeFilter || ""}
            onChange={(val) => setTypeFilter(val ? val : undefined)}
            style={{ width: "160px" }}
            size="large"
            placeholder="Select Type"
          >
            <Select.Option value="">All Types</Select.Option>
            <Select.Option value="Issue">Issue</Select.Option>
            <Select.Option value="Blocked">Block</Select.Option>
          </Select>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-fill overflow-auto position-relative">
        {loading ? (
          <div className="position-absolute top-50 start-50 translate-middle text-center p-5">
            <Spin size="large" tip="Loading data..." />
          </div>
        ) : data.length === 0 ? (
          <Empty description="No issues or blocks found" className="my-5" />
        ) : (
          <Table
            dataSource={data}
            columns={columns}
            rowKey="_id"
            pagination={false}
            scroll={{ x: "max-content" }}
            bordered
            className="custom-table"
            onRow={(record) => ({
              onClick: () => navigate(`/quote-requests/${record.quote_request_id}`),
              style: { cursor: "pointer" }
            })}
          />
        )}
      </div>
    </div>
  );
}
